import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { TranslationService, LOCALIZATION_LOCAL_STORAGE_KEY } from '../../i18n/translation.service';
import { environment } from 'src/environments/environment';

export interface Language {
  code: string;
  name: string;
  flag: string;
  nativeName?: string;
}

/** UI codes → API contact.language values */
const UI_TO_API_LANGUAGE: Record<string, string> = {
  en: 'en_us',
  nl: 'nl_nl',
  de: 'de_de',
  fr: 'fr_fr',
  es: 'es_es',
};

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private currentLanguageSubject = new BehaviorSubject<string>('en');
  public currentLanguage$ = this.currentLanguageSubject.asObservable();

  // Available languages
  private readonly availableLanguages: Language[] = [
    { code: 'en', name: 'English', flag: 'united-states.svg', nativeName: 'English' },
    { code: 'nl', name: 'Dutch', flag: 'netherlands.svg', nativeName: 'Nederlands' }
  ];

  constructor(
    private translationService: TranslationService,
    private http: HttpClient,
  ) {
    // Prefer unified TranslationService key; fall back to legacy selectedLanguage.
    const storedLanguage =
      localStorage.getItem(LOCALIZATION_LOCAL_STORAGE_KEY) ||
      localStorage.getItem('selectedLanguage') ||
      this.translationService.getSelectedLanguage();
    if (storedLanguage) {
      this.setLanguage(this.normalizeUiCode(storedLanguage), { persistRemote: false });
    } else {
      this.initializeFromBrowser();
    }
  }

  getAvailableLanguages(): Language[] {
    return this.availableLanguages;
  }

  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /** Normalize `nl_nl` / `en-US` → `nl` / `en`. */
  normalizeUiCode(languageCode: string): string {
    const raw = (languageCode || '').trim().toLowerCase();
    if (!raw) {
      return 'en';
    }
    const base = raw.split(/[_-]/)[0] || 'en';
    return this.availableLanguages.some((l) => l.code === base) ? base : 'en';
  }

  toApiLanguage(languageCode: string): string {
    const ui = this.normalizeUiCode(languageCode);
    return UI_TO_API_LANGUAGE[ui] || 'en_us';
  }

  setLanguage(languageCode: string, options?: { persistRemote?: boolean }): void {
    languageCode = this.normalizeUiCode(languageCode);

    // Store in both keys so startup paths stay consistent
    localStorage.setItem('selectedLanguage', languageCode);
    this.translationService.setLanguage(languageCode);

    this.currentLanguageSubject.next(languageCode);
    document.documentElement.lang = languageCode;

    if (options?.persistRemote === true) {
      this.persistToContact(languageCode).subscribe();
    }
  }

  /** Persist preference to contact profile so reload/login does not revert it. */
  persistToContact(languageCode: string): Observable<boolean> {
    const token = localStorage.getItem('authToken');
    if (!token) {
      return of(false);
    }
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const body = { language: this.toApiLanguage(languageCode) };
    return this.http.put<any>(`${environment.apiUrl}/auth/contact/language`, body, { headers }).pipe(
      map((resp) => !!resp?.success),
      tap((ok) => {
        if (!ok) {
          console.warn('[LanguageService] Failed to persist language to contact');
        }
      }),
      catchError((err) => {
        console.warn('[LanguageService] Failed to persist language to contact', err);
        return of(false);
      })
    );
  }

  getLanguageByCode(code: string): Language | undefined {
    return this.availableLanguages.find(lang => lang.code === this.normalizeUiCode(code));
  }

  // Prefer an explicit UI choice over contact profile (contact may lag until persist completes).
  initializeFromUser(userLanguage?: string): void {
    const stored =
      localStorage.getItem(LOCALIZATION_LOCAL_STORAGE_KEY) ||
      localStorage.getItem('selectedLanguage');
    if (stored) {
      this.setLanguage(stored, { persistRemote: false });
      return;
    }
    if (userLanguage) {
      this.setLanguage(userLanguage, { persistRemote: false });
    }
  }

  /**
   * Detect preferred browser language and apply it if supported
   */
  initializeFromBrowser(): void {
    const browserLangs: string[] = [];
    if (typeof navigator !== 'undefined') {
      if (Array.isArray((navigator as any).languages) && (navigator as any).languages.length) {
        browserLangs.push(...(navigator as any).languages as string[]);
      }
      if (navigator.language) {
        browserLangs.push(navigator.language);
      }
    }
    const normalizedCandidates = browserLangs
      .map(l => l.toLowerCase())
      .map(l => l.includes('-') ? l.split('-')[0] : (l.includes('_') ? l.split('_')[0] : l));
    const supported = this.availableLanguages.map(l => l.code);
    const match = normalizedCandidates.find(c => supported.includes(c));
    this.setLanguage(match || 'en', { persistRemote: false });
  }
}
